import React from 'react';
import { motion } from 'framer-motion';
import { useApp } from '../../context/AppContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPackage, FiClock, FiCheckCircle, FiXCircle } = FiIcons;

function OrderHistory() {
  const { orders } = useApp();

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return FiClock;
      case 'completed':
        return FiCheckCircle;
      case 'cancelled':
        return FiXCircle;
      default:
        return FiPackage;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'cancelled':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-government-600 bg-government-100';
    }
  };

  if (orders.length === 0) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-8 text-center"
        >
          <SafeIcon icon={FiPackage} className="w-16 h-16 text-government-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-government-900 mb-2">
            No orders yet
          </h2>
          <p className="text-government-600">
            Your order history will appear here once you place your first order
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-government-900 mb-2">
          Order History
        </h1>
        <p className="text-government-600">
          View all your previous orders
        </p>
      </motion.div>

      <div className="space-y-6">
        {orders.map((order, index) => (
          <motion.div
            key={order.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-government-900">
                  Order #{order.id.substring(0, 8)}
                </h3>
                <p className="text-sm text-government-600">
                  {new Date(order.createdAt).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                  <SafeIcon icon={getStatusIcon(order.status)} className="w-4 h-4 inline mr-1" />
                  {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                </span>
                <span className="text-lg font-bold text-primary-600">
                  £{order.total.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              {order.items.map((item) => (
                <div key={item.cartId} className="flex justify-between items-center py-2 border-b border-government-100 last:border-b-0">
                  <div className="flex items-center space-x-3">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-10 h-10 object-cover rounded"
                    />
                    <div>
                      <p className="font-medium text-government-900">
                        {item.name}
                      </p>
                      <p className="text-sm text-government-600">
                        Qty: {item.quantity}
                      </p>
                    </div>
                  </div>
                  <span className="font-medium text-government-900">
                    £{item.total.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default OrderHistory;